import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const ref = useRef(null);

  const loadCount = async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setCount(r.data.count);
    } catch (e) {
      console.error("Failed to load unread count:", e);
    }
  };
  const loadItems = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data);
    } catch (e) {
      console.error("Failed to load notifications:", e);
    }
  };

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 20000);
    const onLive = () => loadCount();
    window.addEventListener("dl-notify", onLive);
    return () => {
      clearInterval(t);
      window.removeEventListener("dl-notify", onLive);
    };
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadItems();
  };

  const openItem = async (it) => {
    try { await api.post(`/notifications/${it.notification_id}/read`); } catch (e) { console.error("Failed to mark read:", e); }
    setOpen(false);
    loadCount();
    if (it.dap_id) navigate(`/dap/${it.dap_id}`);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    loadItems();
    loadCount();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={toggle}
        className="relative text-[#5C5A54] hover:text-[#1A1A1A] transition-colors p-1"
        title="Notifications"
      >
        <Bell size={20} strokeWidth={1.8} />
        {count > 0 && (
          <span
            data-testid="notification-count"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#E07A5F] text-white text-[10px] font-bold flex items-center justify-center"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-3 w-80 sm:w-96 bg-white border border-[#DEDBD3] rounded-xl shadow-lg z-50 overflow-hidden animate-fade-up"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#DEDBD3]">
            <span className="overline text-[#5C5A54]">Notifications</span>
            <button
              data-testid="mark-all-read-btn"
              onClick={markAll}
              className="flex items-center gap-1 text-xs text-[#1B4D3E] hover:text-[#143A2F]"
            >
              <CheckCheck size={14} /> Mark all read
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-[#8A877F]">Nothing yet.</p>
            ) : (
              items.map((it) => (
                <button
                  key={it.notification_id}
                  data-testid={`notification-item-${it.notification_id}`}
                  onClick={() => openItem(it)}
                  className={`w-full text-left px-4 py-3 border-b border-[#F0EBE1] hover:bg-[#F9F8F6] transition-colors ${
                    it.read ? "opacity-60" : ""
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!it.read && (
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-[#E07A5F] shrink-0" />
                    )}
                    <div className={it.read ? "pl-4" : ""}>
                      <p className="text-sm font-medium text-[#1A1A1A]">{it.title}</p>
                      <p className="text-xs text-[#5C5A54] mt-0.5 leading-snug">{it.body}</p>
                      <p className="text-[10px] text-[#A3A095] mt-1">
                        {new Date(it.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
